<script lang="ts">
	import Table from '#lib/components/Table.svelte';
	import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
	import { calcTime } from '../hockeydata.js';
	import type {
		HDFieldPlayer,
		HDGameReportResponseData,
		HDGoalKeeper,
		HDGoalKeeperChange,
		HDPlayer
	} from '../types/responses/GameReport.js';
	import type { ExtendedRow } from '../types/util/ExtendedRow.js';
	import type { HDExtraData } from '../types/util/ExtraData.js';
	import type { TableCol } from '../types/util/TableCol.js';
	import { twMerge } from 'tailwind-merge';
	import { faChevronCircleDown, faChevronCircleUp } from '@fortawesome/free-solid-svg-icons';
	import CollapsibleCard from '#lib/components/CollapsibleCard.svelte';

	let {
		game
	}: {
		game: HDGameReportResponseData;
	} = $props();

	interface Lineup {
		lf?: HDPlayer;
		c?: HDPlayer;
		rf?: HDPlayer;
		ld?: HDPlayer;
		rd?: HDPlayer;
	}

	const getLineup = (players?: any[]): Lineup[] => {
		const lines: Lineup[] = [];
		players?.forEach((player: any) => {
			if (player.position && ['LF', 'C', 'RF', 'LD', 'RD'].includes(player.position)) {
				lines[player.lineupLine - 1] = lines[player.lineupLine - 1] || {};
				lines[player.lineupLine - 1][player.position.toLowerCase() as keyof Lineup] = player;
			}
		});
		return lines;
	};

	interface PlayerData {
		team?: string;
		players?: ExtendedRow<HDFieldPlayer>[];
		goalKeepers?: ExtendedRow<HDGoalKeeper>[];
		goalKeeperChanges?: ExtendedRow<HDGoalKeeperChange>[];
		lineUp: Lineup[];
	}

	let highlightedPlayerId = $state<number>();

	const homePlayers = $derived.by(() =>
		game.homeFieldPlayers.map(
			(player: HDFieldPlayer): ExtendedRow<HDFieldPlayer> => ({
				...player,
				highlight: player.id === highlightedPlayerId
			})
		)
	);

	const awayPlayers = $derived.by(() =>
		game.awayFieldPlayers.map(
			(player: HDFieldPlayer): ExtendedRow<HDFieldPlayer> => ({
				...player,
				highlight: player.id === highlightedPlayerId
			})
		)
	);

	const homeGoalKeepers = $derived.by(() =>
		game.homeGoalKeepers.map(
			(goalKeeper: HDGoalKeeper): ExtendedRow<HDGoalKeeper> => ({
				...goalKeeper
			})
		)
	);

	const homeGoalKeeperChanges = $derived.by(() =>
		game.homeGoalKeeperChanges.map(
			(change: HDGoalKeeperChange): ExtendedRow<HDGoalKeeperChange> => ({
				id: `${change.gameTimePeriod}-${change.gameTimeFormatted}-${change.player.id}`,
				...change
			})
		)
	);

	const awayGoalKeepers = $derived.by(() =>
		game.awayGoalKeepers.map(
			(goalKeeper: HDGoalKeeper): ExtendedRow<HDGoalKeeper> => ({
				...goalKeeper
			})
		)
	);

	const awayGoalKeeperChanges = $derived.by(() =>
		game.awayGoalKeeperChanges.map(
			(change: HDGoalKeeperChange): ExtendedRow<HDGoalKeeperChange> => ({
				id: `${change.gameTimePeriod}-${change.gameTimeFormatted}-${change.player.id}`,
				...change
			})
		)
	);

	const playerData = $derived<PlayerData[]>([
		{
			team: game.gameData.homeTeamShortname,
			players: homePlayers,
			goalKeepers: homeGoalKeepers,
			goalKeeperChanges: homeGoalKeeperChanges,
			lineUp: getLineup(game.homeFieldPlayers)
		},
		{
			team: game.gameData.awayTeamShortname,
			players: awayPlayers,
			goalKeepers: awayGoalKeepers,
			goalKeeperChanges: awayGoalKeeperChanges,
			lineUp: getLineup(game.awayFieldPlayers)
		}
	]);

	const playerCols: TableCol[] = [
		{
			key: 'player',
			label: '',
			sortable: true,
			sortFunction: (o: HDExtraData<HDFieldPlayer>) => `${o.playerLastname} ${o.playerFirstname}`,
			class: 'justify-start'
		},
		{
			key: 'position',
			label: 'POS',
			sortable: true,
			tooltip: 'Position'
		},
		{
			key: 'goals',
			label: 'G',
			sortable: true,
			tooltip: 'Tore'
		},
		{
			key: 'assists',
			label: 'A',
			sortable: true,
			tooltip: 'Assists'
		},
		{
			key: 'penaltyMinutes',
			label: 'PIM',
			sortable: true,
			tooltip: 'Strafminuten'
		}
	];

	const goalieCols: TableCol[] = [
		{
			key: 'player',
			label: ''
		},
		{
			key: 'playingTime',
			label: 'TOI',
			sortable: true,
			tooltip: 'Zeit am Eis'
		},
		{
			key: 'goalsAgainst',
			label: 'GA',
			sortable: true,
			tooltip: 'Gegentore'
		}
	];

	const goalKeeperChangeCols: TableCol[] = [
		{
			key: 'gameTimePeriod',
			label: 'Per',
			sortable: true,
			tooltip: 'Spielabschnitt',
			tooltipClass: 'tooltip-right'
		},
		{
			key: 'gameTimeFormatted',
			label: 'Zeit',
			sortable: true
		},
		{
			key: 'player',
			label: ''
		},
		{
			key: 'onOff',
			label: ''
		}
	];

	const playerTables: { home?: Element; away?: Element } = $state({});

	function highlightPlayer(player: ExtendedRow<HDPlayer>, team?: string) {
		if (!team) {
			return;
		}
		highlightedPlayerId = player.id;

		const home = team === game.gameData.homeTeamShortname;
		if (home) {
			(playerTables.home as Element).scrollIntoView({
				behavior: 'smooth'
			});
		} else {
			(playerTables.away as Element).scrollIntoView({
				behavior: 'smooth'
			});
		}
	}
</script>

{#each playerData as team (team.team)}
	<CollapsibleCard title={'Feldspieler ' + team.team} open>
		<div
			class="mb-1 flex w-full flex-col gap-1 lg:flex-row"
			bind:this={playerTables[team.team === game.gameData.homeTeamShortname ? 'home' : 'away']}
		>
			<Table columns={playerCols} rows={team.players}>
				{#snippet headers(column)}
					{#if column.key === 'player'}
						<div class="flex flex-row items-center">
							<span class="text-accent block w-8 pr-2 text-right">#</span> Spieler
						</div>
					{:else}
						{column.label}
					{/if}
				{/snippet}

				{#snippet data(row, col, getRowData)}
					{#if col.key === 'player'}
						<div class="flex flex-row items-center">
							<span class="text-accent block w-8 pr-2 text-right">{row.playerJerseyNr}</span>
							{row.playerLastname}&nbsp;{row.playerFirstname.charAt(0)}.
						</div>
					{:else}
						<div>{getRowData(row, col.key)}</div>
					{/if}
				{/snippet}
			</Table>
			{#if team.lineUp && team.lineUp.length > 0}
				<div class="bg-base-100 divide-accent flex w-full flex-col divide-y-2 p-3">
					<h3>Linien</h3>
					{#each team.lineUp as lineup, lineupIndex (lineupIndex)}
						<div class="flex w-full flex-col pt-2">
							<h4 class="text-center">Linie {lineupIndex + 1}</h4>
							<div class="flex flex-row justify-around">
								<button
									class={twMerge(
										'bg-neutral border-primary m-4 flex h-16 w-16 flex-col items-center justify-center border-2 text-2xl font-bold',
										lineup.lf
											? 'hover:border-accent cursor-pointer'
											: 'bg-base-100 border-neutral border-2'
									)}
									onclick={() => lineup.lf && highlightPlayer(lineup.lf, team.team)}
								>
									{lineup.lf?.playerJerseyNr ?? '-'}
									<span class="text-primary text-xs">LF</span>
								</button>
								<button
									class={twMerge(
										'bg-neutral border-primary m-4 flex h-16 w-16 flex-col items-center justify-center border-2 text-2xl font-bold',
										lineup.c
											? 'hover:border-accent cursor-pointer'
											: 'bg-base-100 border-neutral border-2'
									)}
									onclick={() => lineup.c && highlightPlayer(lineup.c, team.team)}
								>
									{lineup.c?.playerJerseyNr ?? '-'}
									<span class="text-primary text-xs">C</span>
								</button>
								<button
									class={twMerge(
										'bg-neutral border-primary m-4 flex h-16 w-16 flex-col items-center justify-center border-2 text-2xl font-bold',
										lineup.rf
											? 'hover:border-accent cursor-pointer'
											: 'bg-base-100 border-neutral border-2'
									)}
									onclick={() => lineup.rf && highlightPlayer(lineup.rf, team.team)}
								>
									{lineup.rf?.playerJerseyNr ?? '-'}
									<span class="text-primary text-xs">RF</span>
								</button>
							</div>
							<div class="flex flex-row justify-around">
								<button
									class={twMerge(
										'bg-neutral border-primary m-4 flex h-16 w-16 flex-col items-center justify-center border-2 text-2xl font-bold',
										lineup.ld
											? 'hover:border-accent cursor-pointer'
											: 'bg-base-100 border-neutral border-2'
									)}
									onclick={() => lineup.ld && highlightPlayer(lineup.ld, team.team)}
								>
									{lineup.ld?.playerJerseyNr ?? '-'}
									<span class="text-primary text-xs">LD</span>
								</button>
								<button
									class={twMerge(
										'bg-neutral border-primary m-4 flex h-16 w-16 flex-col items-center justify-center border-2 text-2xl font-bold',
										lineup.rd
											? 'hover:border-accent cursor-pointer'
											: 'bg-base-100 border-neutral border-2'
									)}
									onclick={() => lineup.rd && highlightPlayer(lineup.rd, team.team)}
								>
									{lineup.rd?.playerJerseyNr ?? '-'}
									<span class="text-primary text-xs">RD</span>
								</button>
							</div>
						</div>
					{/each}
				</div>
			{/if}
		</div>
	</CollapsibleCard>

	<CollapsibleCard title={`Goalies - ${team.team}`} open>
		<div class="mb-1 flex w-full flex-col gap-1 md:flex-row">
			<Table columns={goalieCols} rows={team.goalKeepers} class="md:w-1/2">
				{#snippet headers(column)}
					{#if column.key === 'player'}
						<div class="flex flex-row items-center">
							<span class="text-accent block w-8 pr-2 text-right">#</span> Spieler
						</div>
					{:else}
						{column.label}
					{/if}
				{/snippet}

				{#snippet data(row, col, getRowData)}
					{#if col.key === 'player'}
						<div class="flex flex-row items-center">
							<span class="text-accent block w-8 pr-2 text-right">{row.playerJerseyNr}</span>
							{row.playerLastname}&nbsp;{row.playerFirstname.charAt(0)}.
						</div>
					{:else if col.key === 'playingTime'}
						{calcTime(row.playingTime)}
					{:else}
						<div>{getRowData(row, col.key)}</div>
					{/if}
				{/snippet}
			</Table>
			<Table columns={goalKeeperChangeCols} rows={team.goalKeeperChanges} class="md:w-1/2">
				{#snippet headers(column)}
					{#if column.key === 'player'}
						<div class="flex flex-row items-center">
							<span class="text-accent block w-8 pr-2 text-right">#</span> Goalie
						</div>
					{:else}
						{column.label}
					{/if}
				{/snippet}
				{#snippet data(row, col, getRowData)}
					{#if col.key === 'player'}
						<div class="flex flex-row items-center">
							<span class="text-accent block w-8 pr-2 text-right">{row.player.playerJerseyNr}</span>
							{row.player.playerLastname}&nbsp;{row.player.playerFirstname.charAt(0)}.
						</div>
					{:else if col.key === 'onOff'}
						{#if row.action === 'on'}
							<span class="flex flex-row items-center gap-2 font-bold text-green-500 uppercase">
								ON <FontAwesomeIcon icon={faChevronCircleUp} />
							</span>
						{:else if row.action === 'off'}
							<span class="flex flex-row items-center gap-2 font-bold text-red-500 uppercase">
								OFF <FontAwesomeIcon icon={faChevronCircleDown} />
							</span>
						{/if}
					{:else}
						{getRowData(row, col.key)}
					{/if}
				{/snippet}
			</Table>
		</div>
	</CollapsibleCard>
{/each}
